<template>
  <div>
    <NotificationGroup :position="'bottom'">
      <div class="fixed inset-x-0 bottom-0 flex items-start justify-end p-6 px-4 py-6 pointer-events-none z-50">
        <div class="w-full max-w-sm">
          <Notification
              v-slot="{ notifications }"
              enter="ease-out duration-300 transition"
              enter-from="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-4"
              enter-to="translate-y-0 opacity-100 sm:translate-x-0"
              leave="transition ease-in duration-500"
              leave-from="opacity-100"
              leave-to="opacity-0"
              move="transition duration-500"
              move-delay="delay-300"
          >
            <div
                v-for="notification in notifications"
                :key="notification.id"
                class="flex w-full max-w-sm mx-auto mt-4 overflow-hidden bg-white rounded-lg shadow-md"
            >
              <div class="flex items-center justify-center w-1"
                   :class="{'bg-red-500': notification.type === 'error', 'bg-primary': notification.type === 'success', 'bg-gray-500': notification.type === 'info'}"></div>

              <div class="px-4 py-2 -mx-3">
                <div class="mx-3">
                  <span class="font-semibold"
                        :class="{'text-red-500': notification.type === 'error', 'text-primary': notification.type === 'success', 'text-gray-500': notification.type === 'info'}"
                        v-if="notification.title">{{ notification.title }}</span>
                  <p class="text-sm text-gray-600" v-if="notification.text" v-html="notification.text"/>
                </div>
              </div>
            </div>
          </Notification>
        </div>
      </div>
    </NotificationGroup>

    <NotificationGroup group="error" position="bottom">
      <div
          class="fixed inset-0 flex items-start justify-end p-6 px-4 py-6 pointer-events-none"
      >
        <div class="w-full max-w-sm">
          <Notification
              v-slot="{ notifications }"
              enter="transform ease-out duration-300 transition"
              enter-from="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-4"
              enter-to="translate-y-0 opacity-100 sm:translate-x-0"
              leave="transition ease-in duration-500"
              leave-from="opacity-100"
              leave-to="opacity-0"
              move="transition duration-500"
              move-delay="delay-300"
          >
            <div
                class="flex w-full max-w-sm mx-auto mt-4 overflow-hidden bg-white rounded-lg shadow-md"
                v-for="notification in notifications"
                :key="notification.id"
            >
              <div class="flex items-center justify-center w-12 bg-red-500">
                <svg class="w-6 h-6 text-white fill-current" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                  <path
                      d="M20 3.36667C10.8167 3.36667 3.3667 10.8167 3.3667 20C3.3667 29.1833 10.8167 36.6333 20 36.6333C29.1834 36.6333 36.6334 29.1833 36.6334 20C36.6334 10.8167 29.1834 3.36667 20 3.36667ZM19.1334 33.3333V22.9H13.3334L21.6667 6.66667V17.1H27.25L19.1334 33.3333Z"/>
                </svg>
              </div>

              <div class="px-4 py-2 -mx-3">
                <div class="mx-3">
                  <span class="font-semibold text-red-500">{{ notification.title }}</span>
                  <p class="text-sm text-gray-600">{{ notification.text }}</p>
                </div>
              </div>
            </div>
          </Notification>
        </div>
      </div>
    </NotificationGroup>

    <NotificationGroup group="generic">
      <div
          class="fixed inset-0 flex items-start justify-end p-6 px-4 py-6 pointer-events-none"
      >
        <div class="w-full max-w-sm">
          <Notification
              v-slot="{ notifications }"
              enter="transform ease-out duration-300 transition"
              enter-from="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-4"
              enter-to="translate-y-0 opacity-100 sm:translate-x-0"
              leave="transition ease-in duration-500"
              leave-from="opacity-100"
              leave-to="opacity-0"
              move="transition duration-500"
              move-delay="delay-300"
          >
            <div
                class="flex w-full max-w-sm mx-auto mt-4 overflow-hidden bg-white rounded-lg shadow-md"
                v-for="notification in notifications"
                :key="notification.id"
            >
              <div class="flex items-center justify-center w-12 bg-blue-500">
                <svg class="w-6 h-6 text-white fill-current" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                  <path
                      d="M20 3.33331C10.8 3.33331 3.33337 10.8 3.33337 20C3.33337 29.2 10.8 36.6666 20 36.6666C29.2 36.6666 36.6667 29.2 36.6667 20C36.6667 10.8 29.2 3.33331 20 3.33331ZM21.6667 28.3333H18.3334V25H21.6667V28.3333ZM21.6667 21.6666H18.3334V11.6666H21.6667V21.6666Z"/>
                </svg>
              </div>

              <div class="px-4 py-2 -mx-3">
                <div class="mx-3">
                  <span class="font-semibold text-blue-500">{{ notification.title }}Info</span>
                  <p class="text-sm text-gray-600">{{ notification.text }}</p>
                </div>
              </div>
            </div>
          </Notification>
        </div>
      </div>
    </NotificationGroup>


  </div>
</template>

<script>
import {NotificationGroup, Notification} from 'notiwind/dist/index.esm.js'

export default {
  name: "NotificationOverlay",
  components: {NotificationGroup, Notification},
  mounted() {
    if (this.$page.props.jetstream.flash && this.$page.props.jetstream.flash.text) {
      this.$notify({
        title: this.$page.props.jetstream.flash?.title || (this.$page.props.jetstream.flash.type === 'success' ? 'Successfully' : (this.$page.props.jetstream.flash.type === 'error' ? 'Error' : 'Info')),
        text: this.$page.props.jetstream.flash?.text || 'Empty Text',
        type: this.$page.props.jetstream.flash?.type || 'info',
      }, 8000);
    }
  },
  watch: {
    '$page.props.jetstream.flash': {
      handler(flash) {
        console.log(flash)
        if (!flash?.text) return;
        this.$notify({
          title: flash?.title || (flash.type === 'success' ? 'Successfully' : (flash.type === 'error' ? 'Error' : 'Info')),
          text: flash?.text || 'Empty Text',
          type: flash?.type || 'info',
        }, 8000);
      },
      deep: true,
    }
  },
}
</script>
